import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-[0.6] [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 touch-manipulation select-none",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:scale-95",
        outline:
          "border border-input bg-background hover:bg-accent/15 hover:text-purple-700 active:scale-95",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:scale-95",
        ghost: "hover:bg-accent/15 hover:text-purple-700 active:scale-95",
        link: "text-primary underline-offset-4 hover:underline active:scale-95",
        // 📱 MOBILE-FIRST: New mobile-optimized variants
        "mobile-primary": "bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:scale-105 active:scale-95",
        "mobile-secondary": "bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:scale-95",
        "mobile-outline": "border-2 border-pink-700 text-pink-700 rounded-lg hover:bg-pink-50 active:scale-95",
      },
      size: {
        default: "h-10 px-4 py-2 min-h-[44px]", // 📱 44px minimum touch target
        sm: "h-9 rounded-md px-3 min-h-[44px] min-w-[44px]", // 📱 Touch-friendly
        lg: "h-11 rounded-md px-8 min-h-[48px]", // 📱 Comfortable touch
        icon: "h-10 w-10 min-h-[44px] min-w-[44px]", // 📱 Touch-friendly icon
        // 📱 MOBILE-FIRST: New mobile-optimized sizes
        "mobile-sm": "h-11 px-4 py-2 min-h-[44px] min-w-[44px] text-sm",
        "mobile-md": "h-12 px-6 py-3 min-h-[48px] text-base",
        "mobile-lg": "h-14 px-8 py-4 min-h-[56px] text-lg",
        "mobile-icon": "h-12 w-12 min-h-[48px] min-w-[48px] rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref as React.LegacyRef<HTMLButtonElement>}
        style={{
          // 📱 MOBILE: Optimize for touch interactions
          WebkitTapHighlightColor: 'transparent',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          touchAction: 'manipulation',
          ...props.style
        }}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }

